<template>
  <div class="d-flex">
    <navigator />

    <div class="page">
      <div class="title-header">
        <h2>Survey Moderation Rules</h2>
      </div>

      <div class="grid">
        <section class="card">
          <header class="card__header">
            <h3>Blacklisted words</h3>
            <!-- hook: open add-word modal -->
            <b-button class="btn-add" @click="$bvModal.show('word-modal')">Add</b-button>
          </header>

          <input
            class="search"
            type="text"
            v-model="wordQuery"
            placeholder="Search"
            @input="wordsPage = 1"
          />

          <ul class="list" ref="wordsList">
            <li v-for="w in pagedWords" :key="w.text + w.docid" class="list__item">
              <span class="list__text">{{ w.text }}</span>

              <!-- hook: also delete the word in backend -->
              <b-button class="btn-danger" @click="removeWord(w.text, w.docid)">Delete</b-button>
            </li>
            <li v-if="pagedWords.length === 0" class="empty">No results.</li>
          </ul>

          <div class="pagination-container">
            <b-button
              class="pagination-arrow"
              @click="wordsPage = Math.max(1, wordsPage - 1)"
              :disabled="wordsPage === 1"
            >&lt;</b-button>
            <span
              v-for="p in wordsTotalPages"
              :key="'w'+p"
              class="pagination-circle"
              :class="{ active: p === wordsPage }"
              @click="wordsPage = p"
            >{{ p }}</span>
            <b-button
              class="pagination-arrow"
              @click="wordsPage = Math.min(wordsTotalPages, wordsPage + 1)"
              :disabled="wordsPage === wordsTotalPages"
            >&gt;</b-button>
          </div>
        </section>

        <section class="card">
          <header class="card__header">
            <h3>Blacklisted regex patterns</h3>
            <!-- hook: open add-regex modal -->
            <b-button class="btn-add" @click="$bvModal.show('regex-modal')">Add</b-button>
          </header>

          <input
            class="search"
            type="text"
            v-model="regexQuery"
            placeholder="Search"
            @input="regexPage = 1"
          />

          <ol class="regex" ref="regexList">
            <li v-for="(r, i) in pagedRegex" :key="r.id" class="regex__item">
              <div class="regex__title">
                {{ i + 1 + (regexPage - 1) * pageSize }}. {{ r.name }}
              </div>
              <div class="regex__box">
                <pre class="regex__code">{{ r.pattern }}</pre>
                <!-- hook: also delete the regex in backend -->
                <b-button class="btn-danger" @click="removeRegex(r.regexMap, r.docid)">Delete</b-button>
              </div>
            </li>
            <li v-if="pagedRegex.length === 0" class="empty">No results.</li>
          </ol>

          <div class="pagination-container">
            <b-button
              class="pagination-arrow"
              @click="regexPage = Math.max(1, regexPage - 1)"
              :disabled="regexPage === 1"
            >&lt;</b-button>
            <span
              v-for="p in regexTotalPages"
              :key="'r'+p"
              class="pagination-circle"
              :class="{ active: p === regexPage }"
              @click="regexPage = p"
            >{{ p }}</span>
            <b-button
              class="pagination-arrow"
              @click="regexPage = Math.min(regexTotalPages, regexPage + 1)"
              :disabled="regexPage === regexTotalPages"
            >&gt;</b-button>
          </div>
        </section>
      </div>
    </div>

    <b-modal id="word-modal" title="Add Moderation Rule" centered hide-footer dialog-class="rule-modal">
      <!-- individual word add -->
      <section class="modal-block">
        <h3 class="block-title">Add single rule</h3>
        <div class="single-row">
          <input
            class="rule-input"
            v-model.trim="singleWord"
            placeholder=""
            @keyup.enter="confirmAddWord"
          />
          <!-- hook: add a single word in backend-->
          <b-button class="btn-orange" :disabled="!singleWord" @click="confirmAddWord">Add</b-button>
        </div>
      </section>

      <div class="or-divider"><span>OR</span></div>

      <!-- add words through file upload -->
      <section class="modal-block">
        <h3 class="block-title">Import List</h3>

        <div class="file-row">
          <label class="file-btn">
            Choose File
            <input type="file" accept=".txt,.csv" @change="onWordFile" />
          </label>
          <div class="file-label">{{ wordFileName || 'No file chosen' }}</div>
        </div>

        <!-- hook: insert words by uploading files in backend-->
        <b-button class="btn-orange btn-upload" :disabled="!wordFileContent" @click="confirmUploadWords">UPLOAD</b-button>

        <small class="hint">Support .txt / .csv. One per line, or CSV: <code>word</code>.</small>
      </section>
    </b-modal>

    <b-modal id="regex-modal" title="Add Moderation Rule" centered hide-footer dialog-class="rule-modal">
      <!-- add single regex rule -->
      <section class="modal-block">
        <h3 class="block-title">Add single rule</h3>
        <div class="stack">
          <input class="rule-input" v-model.trim="singleRegexName" placeholder="Rule name (e.g. Credit Card number)" />
          <input class="rule-input" v-model.trim="singleRegexPattern" placeholder="Regex pattern (e.g. \\b\\d{4}[- ]?\\d{4}...)" />
        </div>
        <!-- hook: add a single regex in backend -->
        <b-button
          class="btn-orange"
          :disabled="!singleRegexName || !singleRegexPattern"
          @click="confirmAddRegex"
        >Add</b-button>
      </section>

      <div class="or-divider"><span>OR</span></div>

      <!-- add regex rules through file upload -->
      <section class="modal-block">
        <h3 class="block-title">Import List</h3>

        <div class="file-row">
          <label class="file-btn">
            Choose File
            <input type="file" accept=".txt,.csv" @change="onRegexFile" />
          </label>
          <div class="file-label">{{ regexFileName || 'No file chosen' }}</div>
        </div>

        <!-- hook: insert regex rules by files backend -->
        <b-button class="btn-orange btn-upload" :disabled="!regexFileContent" @click="confirmUploadRegex">UPLOAD</b-button>

        <small class="hint">CSV: <code>name,pattern</code> or TXT: one pattern per line.</small>
      </section>
    </b-modal>
  </div>
</template>

<script>
import Moderation from "../../Firestore/Moderation";
import navigator from "../layout/NavBarNew.vue";

export default {
  name: "Blacklist",
  components: { navigator },

  watch: {
    wordsPage() {
      this.resetScroll("wordsList");
    },
    regexPage() {
      this.resetScroll("regexList");
    },
    wordQuery() {
      this.resetScroll("wordsList");
    },
    regexQuery() {
      this.resetScroll("regexList");
    },
  },
  computed: {
  // words
  filteredWords() {
    const q = (this.wordQuery || "").trim().toLowerCase();
    return (this.words || []).filter(w => (w.text || "").toLowerCase().includes(q));
  },
  wordsTotalPages() {
    return Math.max(1, Math.ceil(this.filteredWords.length / this.pageSize));
  },
  pagedWords() {
    const start = (this.wordsPage - 1) * this.pageSize;
    return this.filteredWords.slice(start, start + this.pageSize);
  },

  // regex
  filteredRegex() {
    const q = (this.regexQuery || "").trim().toLowerCase();
    return (this.regexRules || []).filter(r =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.pattern || "").toLowerCase().includes(q)
    );
  },
  regexTotalPages() {
    return Math.max(1, Math.ceil(this.filteredRegex.length / this.pageSize));
  },
  pagedRegex() {
    const start = (this.regexPage - 1) * this.pageSize;
    return this.filteredRegex.slice(start, start + this.pageSize);
  },
},

  data() {
    return {
        // fake date for demo; hook: load from backend
        // words: [
        //   { docid: "w1", text: "curse" },
        //   { docid: "w2", text: "slang" },
        //   { docid: "w3", text: "badword" },
        // ],
        // regexRules: [
        //   { id: "r1", name: "Scams", pattern: String.raw`\b(http|https):\/\/[^\s]+(free-offer|win-prize)\b` },
        //   { id: "r2", name: "Canadian SIN", pattern: String.raw`\b\d{3}[- ]?\d{3}[- ]?\d{3}\b` },
        // ],
      words: [],
      regexRules: [],
      ModerationRules: [],
      unsubscribe: null,

      wordQuery: "",
      regexQuery: "",
      pageSize: 6,
      wordsPage: 1,
      regexPage: 1,

      singleWord: "",
      wordFileName: "",
      wordFileContent: "",

      singleRegexName: "",
      singleRegexPattern: "",
      regexFileName: "",
      regexFileContent: "",
      
      // loading flags
      loadingWords: false,
      loadingRegex: false,
    };
  },

// hook: await real api connection
//import api from "../../api";

//hook: auto load data from backend when page opens
// ⚠️ uncomment this block and remove the fake demo data in data()
//await real API connection (reloadWords / reloadRegex)

  mounted() {
    // sets up a listener to watch for changes in moderation rules in database
    this.unsubscribe = Moderation.watchAllModerationRules(this.setrules)
    // initial load from backend
    // this.reloadWords();
    // this.reloadRegex();
  },

  methods: {
    resetScroll(refName) {
      this.$nextTick(() => {
        const el = this.$refs[refName];
        if (el) el.scrollTop = 0;
      });
    },

      // toast helpers
    toastOK(msg) {
        this.$bvToast && this.$bvToast.toast(msg, {
        title: "Success",
        variant: "success",
        solid: true,
        toaster: "b-toaster-top-right",
        autoHideDelay: 6000,
        });
    },

    toastERR(msg) {
        this.$bvToast && this.$bvToast.toast(msg, {
        title: "Error",
        variant: "danger",
        solid: true,
        toaster: "b-toaster-top-right",
        autoHideDelay: 6000,
        });
    },

    /**
     * A callback function that is called whenever there is an update to the "ModerationRules" database collection.
     * Updates the local state with the new moderation rules, including words and regex rules.
     * @param {Object} res - The result object containing the success status and data.
     */
    setrules(res) {
      if (res.success) {
        this.ModerationRules = res.data;
      }   
      this.words = [];
      this.regexRules = [];
      this.ModerationRules.forEach(rule => {
        rule.words.forEach(word => {
          this.words.push({ docid: rule.docID, text: word });
        });
        rule.regexRules.forEach(regexMap => {
          this.regexRules.push({ docid: rule.docID, regexMap: regexMap , name: regexMap.name, pattern: regexMap.pattern });
        });
      });
    },

    async reloadWords() {
      // Hook: backend fetch for blacklisted words
      try {
        this.loadingWords = true;
        //...
        this.words = []; // fallback while API not connected
      } catch (e) {
        this.toastERR('Failed to load words');
        this.words = [];
      } finally {
        this.loadingWords = false;
      }
    },

    async reloadRegex() {
      // Hook: backend fetch for regex rules
      try {
        this.loadingRegex = true;
        //....
        this.regexRules = []; // fallback while API not connected
      } catch (e) {
        this.toastERR('Failed to load regex rules');
        this.regexRules = [];
      } finally {
        this.loadingRegex = false;
      }
    },


    async confirmAddWord() {
      if (!this.singleWord) return;
      try {
        // Hook: add a single word in backend
        await Moderation.addBlacklistWord(this.singleWord);
        this.singleWord = '';
        this.$bvModal.hide('word-modal');
        // await this.reloadWords(); 
        this.toastOK('Word added');
      } catch (e) {
        this.toastERR('Failed to add word');
      }
    },

    onWordFile(e) {
      // front-end file read stays here
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      this.wordFileName = file.name;

      const reader = new FileReader();
      reader.onload = () => { this.wordFileContent = String(reader.result || ''); };
      reader.onerror = () => { this.wordFileContent = ''; this.toastERR('Failed to read file'); };
      reader.readAsText(file);
    },

    async confirmUploadWords() {
      if (!this.wordFileContent) return;

      try {
        // simple parse: CSV/TXT, one word per line
        const lines = this.wordFileContent.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
        if (!lines.length) return this.toastERR('No valid content');

        // Hook: add words by files in backend
        if (this.wordFileName) {
          await Moderation.addBlacklistWordList(lines, this.wordFileName);
        } else {
          this.toastERR('Upload failed: missing filename');
        }
        this.wordFileContent = '';
        this.wordFileName = '';
        this.$bvModal.hide('word-modal');
        // await this.reloadWords();
        this.toastOK('Upload success');
      } catch (e) {
        this.toastERR('Upload failed');
      }
    },

    async removeWord(word, docid) {
      try {
        // Hook: delete the word in backend
        await Moderation.deleteBlacklistWord(word, docid);
        // await this.reloadWords();
        this.toastOK('Word deleted');
      } catch (e) {
        this.toastERR('Failed to delete word');
      }
    },

    async confirmAddRegex() {
      if (!this.singleRegexName || !this.singleRegexPattern) return;

      try {
        // Hook: add a single regex rule in backend  
        await Moderation.addBlacklistRegex(this.singleRegexPattern, this.singleRegexName);
        this.singleRegexName = '';
        this.singleRegexPattern = '';
        this.$bvModal.hide('regex-modal');
        // await this.reloadRegex();
        this.toastOK('Regex rule added');
      } catch (e) {
        this.toastERR('Failed to add regex rule');
      }
    },

    onRegexFile(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      this.regexFileName = file.name;

      const reader = new FileReader();
      reader.onload = () => { this.regexFileContent = String(reader.result || ''); };
      reader.onerror = () => { this.regexFileContent = ''; this.toastERR('Failed to read file'); };
      reader.readAsText(file);
    },

    async confirmUploadRegex() {
      if (!this.regexFileContent) return;

      try {
        const rows = this.regexFileContent.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
        if (!rows.length) return this.toastERR('No valid content');

        const payload = rows.map(row => {
          if (row.includes(',')) {
            const [name, ...rest] = row.split(',');
            return { name: (name || '').trim(), pattern: rest.join(',').trim() };
          }
          return { name: '', pattern: row };
        }).filter(r => r.pattern);

        if (!payload.length) return this.toastERR('No valid rows');

        // Hook:add regex rules by files in backend
        if (this.regexFileName){
          await Moderation.addBlacklistRegexList(payload, this.regexFileName)
        } else {
          this.toastERR('Upload failed: missing filename');
        }

        this.regexFileContent = '';
        this.regexFileName = '';
        this.$bvModal.hide('regex-modal');
        // await this.reloadRegex();
        this.toastOK('Upload success');
      } catch (e) {
        this.toastERR('Upload failed');
      }
    },

    async removeRegex(regexMap, docid) {
      try {
        // Hook: delete the regex rule in backend
        await Moderation.deleteBlacklistRegex(regexMap, docid);
        // await this.reloadRegex();
        this.toastOK('Regex rule deleted');
      } catch (e) {
        this.toastERR('Failed to delete regex rule');
      }
    },
  },
};
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  width: 100%;
  height: 100vh;
  max-height: 100vh;
  background: #EEEEEE;
}

.title-header {
  background: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 90px;
  width: 100%;
  box-shadow: 0 4px 10px -6px grey;
}

.title-header h2 {
  color: #FF7222;
  font-weight: 700;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  padding: 20px 24px;
  height: calc(100vh - 90px);
  overflow: auto;
}

.card {
  background: #fff;
  border-radius: 30px;
  padding: 18px 18px 14px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, .1);
  min-height: 300px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.list,
.regex {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: auto;
  padding-top: 10px;
  flex-shrink: 0;
}
.pagination-circle {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background-color: #f1f1f1;
  color: #333;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  cursor: pointer;
  border: 1px solid #ddd;
  transition: background-color 0.3s ease, color 0.3s ease;
}
.pagination-circle.active {
  background-color: #f97427;
  color: white;
  border-color: rgba(0, 0, 0, 0.4);
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
}
.pagination-circle:hover:not(.active) {
  background-color: #e9ecef;
  border-color: rgba(0, 0, 0, 0.3);
}
.pagination-arrow {
  width: 35px;
  height: 35px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  font-size: 1rem;
  border: 1px solid #ddd;
  background-color: #f1f1f1;
  color: #333;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.3s ease, color 0.3s ease;
}
.pagination-arrow:hover:not(:disabled) {
  background-color: #e9ecef;
  border-color: rgba(0, 0, 0, 0.3);
}
.pagination-arrow:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.card__header h3 {
  color: #FF7222;
  font-weight: 800;
}

.btn-add {
  background: #FF7222 !important;
  color: #fff !important;
  border: 0 !important;
  border-radius: 12px !important;
  padding: 8px 16px !important;
  font-weight: 700 !important;
  box-shadow: 0 6px 16px rgba(255, 114, 34, .25);
}

.btn-add:hover {
  background: #FF8E4D !important;
}

/* search input */
.search {
  width: 100%;
  height: 44px;
  background: #E8E8E8;
  border: none;
  border-radius: 12px;
  padding: 0 14px;
  outline: none;
  color: #333;
  margin: 10px 0 8px;
}

/* left list */
.list {
  list-style: none;
  padding: 0;
  margin: 8px 0;
}

.list__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border: 1px solid #eee;
  border-radius: 12px;
  margin-bottom: 10px;
}

.list__text {
  color: #333;
  font-weight: 600;
}

.btn-danger {
  background: #DB3360 !important;
  color: #fff !important;
  border: 0 !important;
  border-radius: 10px !important;
}

.btn-danger:hover {
  filter: brightness(1.05);
}

/* pager */
.pager {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}

.pager__btn {
  background: #E8E8E8 !important;
  border: 0 !important;
  border-radius: 10px !important;
  padding: 6px 12px !important;
  color: #333 !important;
  font-weight: 700 !important;
}

.pager__btn.active {
  background: #FF7222 !important;
  color: #fff !important;
}

/* right regex section */
.regex {
  list-style: none;
  padding-left: 18px;
  margin: 8px 0;
}

.regex__item {
  margin-bottom: 12px;
}

.regex__title {
  font-weight: 800;
  color: #333;
  margin-bottom: 6px;
}

.regex__box {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #F7F7F7;
  border-radius: 12px;
  padding: 10px 12px;
}

.regex__code {
  margin: 0;
  white-space: pre-wrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  color: #3A19A6;
  flex: 1;
}

.empty {
  color: #777;
  text-align: center;
  padding: 16px 0;
}

/* modal */
.rule-modal .modal-content {
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, .08);
}

.rule-modal .modal-title {
  color: #333;
  font-weight: 700;
}

/* block title */
.block-title {
  color: #FF7222;
  font-weight: 800;
  font-size: 24px;
  margin: 0 0 12px;
}

/* single-row input */
.single-row {
  display: flex;
  gap: 16px;
  align-items: center;
}

.stack {
  display: flex;
  gap: 10px;
  flex-direction: column;
  margin-bottom: 8px;
}

.rule-input {
  width: 100%;
  height: 48px;
  background: #E8E8E8;
  border: none;
  border-radius: 10px;
  padding: 0 14px;
  outline: none;
}

/* OR divider */
.or-divider {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 18px 0;
}

.or-divider::before,
.or-divider::after {
  content: "";
  flex: 1;
  height: 2px;
  background: #6D47E6;
  opacity: .35;
  border-radius: 1px;
}

.or-divider span {
  font-weight: 700;
  color: #333;
}

/* file upload */
.file-row {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: 8px;
}

.file-btn {
  position: relative;
  overflow: hidden;
  background: #FF7222;
  color: #fff;
  padding: 10px 18px;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(255, 114, 34, .25);
}

.file-btn input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.file-label {
  flex: 1;
  height: 48px;
  display: flex;
  align-items: center;
  padding: 0 14px;
  border-radius: 12px;
  background: #E8E8E8;
  color: #777;
}

.btn-orange {
  background: #FF7222 !important;
  color: #fff !important;
  border: 0 !important;
  border-radius: 12px !important;
  padding: 10px 18px !important;
  font-weight: 700 !important;
  box-shadow: 0 6px 16px rgba(255, 114, 34, .25);
}

.btn-orange:hover {
  background: #FF8E4D !important;
}

.btn-orange:disabled {
  background: #ffb68f !important;
  box-shadow: none !important;
}

.btn-upload {
  margin-top: 16px;
}

.hint {
  display: block;
  margin-top: 8px;
  color: #777;
}

.modal-block {
  margin-bottom: 8px;
}
</style>



// WEBPACK FOOTER //
// src/components/admin-only/Blacklist.vue